// Verbatim working-draft legal content from "NISCHINT Privacy Policy, Terms of
// Service & DPDP Consent" (Sep 25, 2026). [PENDING COUNSEL REVIEW] values are
// intentional — do not invent values for them.

export const PRIVACY = {
  title: "Privacy Policy",
  testId: "privacy-page",
  effective: "Effective date: 28 October 2026 (beta version 2026-10-beta)",
  sections: [
    {
      h: "1.1 Who we are",
      blocks: [
        { type: "p", text: `NISCHINT is operated by Skybyte Venture Private Limited ("Skybyte," "we," "us"), a company incorporated in India (CIN U66190MH2025PTC456397, PAN ABRCS2404B, GSTIN 27ABRCS2404B1ZJ), with its registered office at A-102, Acme Avenue CHS Ltd, Charkop Sector, Kandivali West, Mumbai 400067, Maharashtra, India. This Privacy Policy explains how we collect, use, share, and protect personal data through the NISCHINT platform (website, mobile app, and related services).` },
      ],
    },
    {
      h: "1.2 What data we collect",
      blocks: [
        {
          type: "list",
          items: [
            `Account data: name, phone number, email address, city, and the relationship you set up (self, guardian, family member).`,
            `Location data: real-time and historical GPS location, used to establish your normal movement pattern and detect meaningful deviations from it.`,
            `Behavioral pattern data: app usage timing, route and stop patterns, and other signals our AI engine (referred to internally as Hermes) uses to learn what "normal" looks like for you and flag deviations.`,
            `Device data: device type, OS version, app version, and diagnostic/crash data.`,
            `Guardian and emergency-contact data: names and contact details of people you designate to receive alerts about you.`,
            `Persona-specific data: where applicable to your account type — for a senior/independent-living account, presence and activity pattern data; for a child-safety account (once live), pickup-authorization and verification data for the adults you designate. NISCHINT does not currently offer a live child-safety persona; this category applies only once that feature ships, and will carry its own verifiable-guardian-consent flow under the DPDP Act's provisions for children's data.`,
            `Payment data: for paid plans, processed by our payment processor — we do not store full card numbers.`,
          ],
        },
        { type: "p", text: `We do not collect more than the above unless we tell you and get your consent first.` },
      ],
    },
    {
      h: "1.3 Why we collect it (purpose limitation)",
      blocks: [
        { type: "p", text: `We use your data only for: operating the core safety function (building and updating your behavioral baseline, detecting deviations, generating and routing alerts); account management and support; legal and regulatory compliance; and, with separate consent, product improvement and communications. We do not use safety data collected for alerting to build advertising profiles or sell it to third parties.` },
      ],
    },
    {
      h: "1.4 Legal basis and consent",
      blocks: [
        { type: "p", text: `We process your personal data on the basis of your consent, given when you create a NISCHINT account and accept this policy, as required under India's Digital Personal Data Protection Act, 2023 (DPDP Act). You can withdraw consent at any time (see Section 3, DPDP Consent Notice) — withdrawal does not affect processing already carried out, and may mean we can no longer provide the safety-alerting function to you.` },
      ],
    },
    {
      h: "1.5 How we share your data",
      blocks: [
        {
          type: "list",
          items: [
            `With your designated guardians/emergency contacts: alert data and, where you've enabled it, your location, in the event of a detected deviation or an emergency.`,
            `With volunteers or responders, only where you've opted into community/volunteer response, and only the minimum information needed to respond.`,
            `With service providers (cloud hosting, SMS/push notification providers, payment processors) under contractual confidentiality and data-processing terms — they may not use your data for their own purposes.`,
            `With law enforcement or government authorities where required by law, or to protect life or safety in an emergency.`,
          ],
        },
        { type: "p", text: `Website analytics: this website uses PostHog (PostHog, Inc., United States) to collect page views, clicks, approximate location derived from your IP address, and device and browser type. Session recording is off, and form input values are never captured. Analytics records are kept for up to 12 months and then deleted or anonymized. SOS live-location pages (links beginning /s/) carry no analytics at all.` },
        { type: "p", text: `We do not sell personal data. We do not share data with the Nischint Sewa Foundation (our associated nonprofit) except through an explicit, separately-consented integration — no such integration is live today.` },
      ],
    },
    {
      h: "1.6 Data retention",
      blocks: [
        { type: "p", text: `We retain your data for as long as your account is active, plus [PENDING COUNSEL REVIEW — retention period] after closure for legal/audit purposes, unless a longer period is required by law. On a verified erasure request we anonymize or delete personal data per Section 3.5, subject to any legal retention obligation.` },
      ],
    },
    {
      h: "1.7 Security",
      blocks: [
        { type: "p", text: `We use encryption in transit and at rest, role-based access controls, and audit logging on systems that touch personal or location data. Our security practices are documented internally in an Access Control Policy and Incident Response Plan; our ISO/IEC 27001:2022 certification is in progress and not yet complete — we do not claim certification until it is issued.` },
      ],
    },
    {
      h: "1.8 Your rights",
      blocks: [
        { type: "p", text: `See Section 3 (DPDP Consent Notice) for your rights as a Data Principal under the DPDP Act and how to exercise them.` },
      ],
    },
    {
      h: "1.9 Children's data",
      blocks: [
        { type: "p", text: `NISCHINT's live personas today (women's safety, independent-living seniors) are for adult account holders. A dedicated child-safety persona is in development and not yet live. When it ships, we will process a minor's data only with verifiable consent from a parent or lawful guardian, as required under Section 9 of the DPDP Act, and this policy will be updated accordingly before that feature launches — not after.` },
      ],
    },
    {
      h: "1.10 Grievance Officer / contact",
      blocks: [
        { type: "p", text: `For privacy questions, complaints, or to exercise your rights, contact our Grievance Officer: Lokesh Thada, privacy@nischint.care, [PENDING COUNSEL REVIEW — registered address]. We aim to acknowledge within [PENDING COUNSEL REVIEW — acknowledgement timeline] and resolve within the timeline required under the DPDP Act.` },
      ],
    },
    {
      h: "1.11 Changes to this policy",
      blocks: [
        { type: "p", text: `We'll notify registered users of material changes before they take effect and update the effective date above.` },
      ],
    },
  ],
};

export const TERMS = {
  title: "Terms of Service",
  testId: "terms-page",
  effective: "Effective date: 28 October 2026 (beta version 2026-10-beta)",
  sections: [
    {
      h: "2.1 Acceptance",
      blocks: [
        { type: "p", text: `By creating a NISCHINT account or using the NISCHINT app or website, you agree to these Terms of Service and the Privacy Policy. If you don't agree, don't use the service.` },
      ],
    },
    {
      h: "2.2 What NISCHINT is — and isn't",
      blocks: [
        { type: "p", text: `NISCHINT is a risk-signal and alerting layer. It is not a guaranteed-prevention, emergency-response, or rescue service, and it does not replace police, ambulance, or other emergency services. NISCHINT uses behavioral pattern learning to flag deviations that may indicate risk and to route alerts to the guardians, contacts, or responders you've designated. Alert delivery depends on factors outside our control, including your device's connectivity, location accuracy, battery, and third-party network/notification services. We do not guarantee that every risk will be detected, that every alert will be delivered, or that any particular response time will be met. In a genuine emergency, always contact local emergency services directly.` },
      ],
    },
    {
      h: "2.3 Eligibility and accounts",
      blocks: [
        { type: "p", text: `You must be at least 18 to create a NISCHINT account, or have it created by a parent/guardian on your behalf where a minor-eligible feature is live. You're responsible for keeping your account credentials secure and for the accuracy of the information you provide, including your emergency contacts' consent to be contacted.` },
      ],
    },
    {
      h: "2.4 Subscription, billing, and refunds",
      blocks: [
        { type: "p", text: `Paid plans, where offered, are billed monthly in advance through our payment processor. You can cancel anytime; cancellation stops future billing but doesn't refund the current billing period, except as set out below.` },
        {
          type: "list",
          items: [
            `Refunds: [PENDING COUNSEL REVIEW — refund window/policy]. Refund requests go to support@nischint.care.`,
          ],
        },
        { type: "p", text: `Prices and plans may change with notice before your next billing cycle.` },
      ],
    },
    {
      h: "2.5 Your responsibilities",
      blocks: [
        { type: "p", text: `You agree to: provide accurate account and contact information; not use NISCHINT to surveil or track another adult without their knowledge and consent (except a lawful guardian-minor relationship); not attempt to reverse-engineer, scrape, or interfere with the service; and not use the service for any unlawful purpose.` },
      ],
    },
    {
      h: "2.6 Intellectual property",
      blocks: [
        { type: "p", text: `NISCHINT, the Hermes engine, and related technology are the property of Skybyte Venture Private Limited. Skybyte has filed patent applications covering core platform mechanisms (geofence-based deviation detection, pickup authorization/verification, and multi-modal distress detection); these are patent-pending, not granted, and we make no claim of granted-patent status anywhere in our marketing or these Terms.` },
      ],
    },
    {
      h: "2.7 Disclaimers and limitation of liability",
      blocks: [
        { type: "p", text: `To the maximum extent permitted by law: NISCHINT is provided "as is" without warranties of any kind, express or implied, including fitness for a particular purpose. Skybyte Venture Private Limited's total liability arising from your use of NISCHINT, however framed, is limited to [PENDING COUNSEL REVIEW — liability cap]. We are not liable for indirect, incidental, or consequential damages, or for any harm arising from a failure, delay, or inaccuracy of an alert.` },
      ],
    },
    {
      h: "2.8 Indemnification",
      blocks: [
        { type: "p", text: `You agree to indemnify Skybyte against claims arising from your misuse of the service or violation of these Terms.` },
      ],
    },
    {
      h: "2.9 Termination",
      blocks: [
        { type: "p", text: `We may suspend or terminate accounts that violate these Terms, misuse the platform, or pose a risk to other users. You may close your account at any time; see Section 1.6 of the Privacy Policy for what happens to your data afterward.` },
      ],
    },
    {
      h: "2.10 Governing law and disputes",
      blocks: [
        { type: "p", text: `These Terms are governed by the laws of India. Courts in Mumbai, Maharashtra have exclusive jurisdiction over any dispute, subject to any mandatory consumer-protection or DPDP grievance-redressal process that applies first.` },
      ],
    },
    {
      h: "2.11 Changes to these Terms",
      blocks: [
        { type: "p", text: `We'll notify registered users of material changes before they take effect.` },
      ],
    },
  ],
};

export const DPDP = {
  title: "DPDP Consent Notice",
  testId: "dpdp-page",
  effective: null,
  intro: `Written to satisfy the notice requirement under Section 5 of India's Digital Personal Data Protection Act, 2023 (DPDP Act). This is the plain-language consent surface — the Privacy Policy has the full detail.`,
  sections: [
    {
      h: "3.1 What the DPDP Act means for you",
      blocks: [
        { type: "p", text: `The DPDP Act gives you (the "Data Principal") control over your personal data. NISCHINT (the "Data Fiduciary," operated by Skybyte Venture Private Limited) must tell you clearly what we collect, why, and get your consent — and you can withdraw that consent, ask what we hold, ask us to correct or erase it, and complain if we don't honor these rights.` },
      ],
    },
    {
      h: "3.2 What we're asking consent for",
      blocks: [
        { type: "p", text: `When you sign up, we ask consent to process:` },
        {
          type: "list",
          items: [
            `Location data — Purpose: Behavioral baseline learning and deviation/risk detection`,
            `Behavioral/usage pattern data — Purpose: Same as above — core safety function`,
            `Contact and account data — Purpose: Account operation, alert routing to your designated contacts`,
            `Guardian/emergency-contact data — Purpose: Delivering alerts on your behalf`,
          ],
        },
        { type: "p", text: `Each purpose is separate — for example, consenting to safety alerting doesn't automatically consent to marketing communications, which we ask for separately and which you can decline without losing the safety features.` },
      ],
    },
    {
      h: "3.3 How to give or withdraw consent",
      blocks: [
        { type: "p", text: `You give consent by completing account setup and accepting this notice. You can withdraw consent at any time from Account Settings → Privacy → [PENDING COUNSEL REVIEW — in-app path once built], or by emailing privacy@nischint.care. Withdrawal takes effect going forward; it doesn't undo processing already done, and if you withdraw consent for location/pattern data specifically, we may not be able to provide the core safety-alerting function — we'll tell you exactly what stops working before you confirm.` },
      ],
    },
    {
      h: "3.4 Your rights as a Data Principal",
      blocks: [
        {
          type: "list",
          items: [
            `Access: ask what personal data we hold about you.`,
            `Correction: ask us to fix inaccurate or incomplete data.`,
            `Erasure: ask us to delete data we no longer need it for, subject to legal retention requirements.`,
            `Grievance redressal: complain to our Grievance Officer (below), and if unresolved, escalate to the Data Protection Board of India.`,
            `Nominate: name someone to exercise these rights on your behalf if you're unable to (e.g., in case of incapacity).`,
          ],
        },
      ],
    },
    {
      h: "3.5 How we handle an erasure request",
      blocks: [
        { type: "p", text: `On a verified request, our default approach is to anonymize rather than hard-delete records where aggregate data (not tied back to you) needs to be retained for safety-pattern research or reporting integrity — personal identifiers are stripped, the underlying record is unlinked from you. Where full deletion is legally required and no retention obligation applies, we delete. [PENDING COUNSEL REVIEW — erasure mechanism to be confirmed as final policy]` },
      ],
    },
    {
      h: "3.6 Consent for minors",
      blocks: [
        { type: "p", text: `Where a NISCHINT feature is designed for a minor (not live at launch — see Section 1.9 of the Privacy Policy), we require verifiable consent from a parent or lawful guardian before processing that minor's data, per Section 9 of the DPDP Act. The guardian, not the minor, is the Data Principal exercising rights on the minor's behalf.` },
      ],
    },
    {
      h: "3.7 Grievance Officer",
      blocks: [
        { type: "p", text: `Lokesh Thada, Grievance Officer, Skybyte Venture Private Limited — privacy@nischint.care — [PENDING COUNSEL REVIEW — registered address]. We will acknowledge your grievance within [PENDING COUNSEL REVIEW — acknowledgement timeline] and aim to resolve it within the timeline set by the DPDP Act and its rules.` },
      ],
    },
  ],
};

// Public account-deletion instructions (Google Play requires a public URL).
// No fixed day-counts anywhere: retention periods are pending legal review.
export const DELETE_ACCOUNT = {
  title: "Delete your NISCHINT account",
  testId: "delete-account-page",
  effective: null,
  sections: [
    {
      h: "Delete in the app",
      blocks: [
        { type: "p", text: "Open NISCHINT → Account → Delete account. You'll confirm with a one-time code sent to your phone." },
      ],
    },
    {
      h: "Can't use the app?",
      blocks: [
        { type: "p", text: "Email privacy@nischint.care from any address, with the subject \"Delete my account\" and your registered mobile number. We'll verify it's you by a code sent to that number, then delete the account." },
      ],
    },
    {
      h: "What gets deleted",
      blocks: [
        {
          type: "list",
          items: [
            "Your profile (name, phone number, date of birth)",
            "Circle memberships",
            "Location history",
            "Consents and devices",
          ],
        },
        { type: "p", text: "Other circle members only see that a member left." },
      ],
    },
    {
      h: "What we may keep",
      blocks: [
        {
          type: "list",
          items: [
            "Records of an SOS incident that is under a legal hold — kept only as long as the law requires, and then erased.",
            "A minimal audit record that a deletion happened, with no personal details.",
          ],
        },
      ],
    },
    {
      h: "Note",
      blocks: [
        { type: "p", text: "If you own a circle with other members, make someone else the owner first. Accounts for children are removed by their parent." },
      ],
    },
  ],
};
